import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LogOut, Menu, Home } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { ChangePasswordDialog } from "@/components/AccountCard";

function NavList({ items, onNavigate }) {
  return (
    <nav className="flex flex-col gap-1">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          onClick={onNavigate}
          data-testid={`nav-${it.id}`}
          className={({ isActive }) =>
            cn("flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
              isActive ? "bg-emerald-700 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white")
          }
        >
          <it.icon className="w-4 h-4" />
          {it.label}
        </NavLink>
      ))}
    </nav>
  );
}

function SideContent({ items, title, subtitle, onNavigate }) {
  const { session, logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="flex flex-col h-full p-5 bg-slate-900 text-slate-100">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center"><Home className="w-4 h-4 text-white" /></div>
        <span className="font-display text-xl font-extrabold tracking-tight">PropNest</span>
      </div>
      <div className="mt-6 mb-6 px-3 py-3 rounded-lg bg-slate-800/60 border border-slate-800">
        <div className="text-sm font-semibold truncate" data-testid="shell-title">{title}</div>
        {subtitle && <div className="text-xs text-slate-400 mt-0.5 truncate">{subtitle}</div>}
      </div>
      <div className="flex-1 overflow-y-auto"><NavList items={items} onNavigate={onNavigate} /></div>
      <div className="pt-4 border-t border-slate-800 mt-4">
        <div className="text-xs text-slate-400 truncate mb-2 px-1" data-testid="shell-user-email">{session?.user?.email}</div>
        <button
          data-testid="logout-button"
          onClick={async () => { await logout(); nav("/login"); }}
          className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
        >
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </div>
    </div>
  );
}

export function Shell({ items, title, subtitle, children }) {
  const [open, setOpen] = useState(false);
  const { session } = useAuth();
  const stopImp = async () => {
    await api.post("/auth/stop-impersonation").catch(() => {});
    window.location.assign("/admin/businesses");
  };
  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {session?.impersonator && (
        <div className="sticky top-0 z-50 bg-amber-400 text-slate-900 text-sm px-4 py-2 flex flex-wrap items-center justify-center gap-3" data-testid="impersonation-banner">
          <span>You're logged in as <b>{session.business?.name || session.user.email}</b> (super admin {session.impersonator})</span>
          <button onClick={stopImp} className="px-3 py-1 rounded-full bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800" data-testid="stop-impersonation-button">Return to admin</button>
        </div>
      )}
    <div className="flex-1 flex">
      <aside className="hidden lg:block w-64 shrink-0 sticky top-0 h-screen">
        <SideContent items={items} title={title} subtitle={subtitle} />
      </aside>
      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200 px-4 h-14 flex items-center justify-between">
          <span className="font-display text-lg font-extrabold">PropNest</span>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-button"><Menu className="w-5 h-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72 border-0">
              <SideContent items={items} title={title} subtitle={subtitle} onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
        </header>
        <main className="p-4 sm:p-6 lg:p-10 max-w-[1400px]">{children}</main>
      </div>
      <ChangePasswordDialog forced open={!!session?.user?.must_change_password && !session?.impersonator} />
    </div>
    </div>
  );
}
